import { useState, useEffect, memo } from 'react';
import { Link } from 'react-router-dom';
import {
  Gavel, Loader, ArrowRight, ExternalLink, Clock, Globe, Server,
  CheckCircle, XCircle, AlertTriangle, Bot, FileText, UserPlus, Ban,
} from 'lucide-react';
import { appealsApiService } from '../../services/appealsApi';
import { AppealsByPlayer } from '../../types/appeals';
import { AppealStatusBadge } from './AppealStatusBadge';
import { AppealVersionBadge } from './AppealVersionBadge';
import { getV2Outcome, V2_OUTCOME_META } from '../../utils/appealsV2';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '../../utils/cn';
import { decodeHtmlEntities } from '../../utils/decodeHtmlEntities';
import { SectionHeader } from '../ui/SectionHeader';
import { SkeletonLoader } from '../SkeletonLoader';

interface PlayerAppealsSectionProps {
  userUuid: string;
  className?: string;
}

type Phase = 'staff' | 'bot' | 'terminal';

const STATUS_PHASE: Record<string, Phase> = {
  pending: 'staff',
  awaiting_staff: 'staff',
  staff_answered: 'bot',
  in_jury: 'bot',
  jury_decided: 'bot',
  approved: 'terminal',
  denied: 'terminal',
  escalated: 'terminal',
  cancelled: 'terminal',
};

const STATUS_ACTION: Record<string, string> = {
  pending: 'Needs staff assignment',
  awaiting_staff: 'Awaiting staff answers',
  staff_answered: 'With Mr.Roboto',
  in_jury: 'Jury deliberating',
  jury_decided: 'Mr.Roboto finalizing',
  approved: 'Ban overturned',
  denied: 'Ban upheld',
  escalated: 'Escalated for review',
  cancelled: 'Cancelled',
};

const PhaseIcon = ({ status }: { status: string }) => {
  const phase = STATUS_PHASE[status];
  if (phase === 'staff') return <FileText className="w-3.5 h-3.5 text-orange-400" />;
  if (phase === 'bot') return <Bot className="w-3.5 h-3.5 text-purple-400" />;
  if (status === 'approved') return <CheckCircle className="w-3.5 h-3.5 text-green-400" />;
  if (status === 'denied') return <XCircle className="w-3.5 h-3.5 text-red-400" />;
  if (status === 'escalated') return <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />;
  return <Ban className="w-3.5 h-3.5 text-glass-400" />;
};

export const PlayerAppealsSection = memo(({ userUuid, className }: PlayerAppealsSectionProps) => {
  const [appeals, setAppeals] = useState<AppealsByPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userUuid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    appealsApiService.getAppealsByPlayer(userUuid)
      .then(data => {
        if (!cancelled) setAppeals(data);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load appeals');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [userUuid]);

  if (loading) {
    return (
      <div className={cn('glass-card p-4 md:p-5', className)} aria-busy="true">
        <SectionHeader
          title="Ban Appeals"
          icon={Gavel}
          iconClassName="text-purple-400"
          actions={<Loader className="w-4 h-4 animate-spin text-glass-400" aria-label="Loading appeals" />}
        />
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="subcard p-3 space-y-2">
              <SkeletonLoader className="h-4 w-40" />
              <SkeletonLoader className="h-3 w-full max-w-md" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('glass-card p-4 md:p-5', className)}>
        <SectionHeader title="Ban Appeals" icon={Gavel} iconClassName="text-purple-400" />
        <p className="text-sm text-glass-400">{error}</p>
      </div>
    );
  }

  const activeAppeals = appeals.filter(a => !['approved', 'denied', 'escalated', 'cancelled'].includes(a.status));
  const closedAppeals = appeals.filter(a => ['approved', 'denied', 'escalated', 'cancelled'].includes(a.status));
  const approvedCount = appeals.filter(a => a.status === 'approved').length;
  const deniedCount = appeals.filter(a => a.status === 'denied').length;

  return (
    <div className={cn('glass-card p-4 md:p-5', className)}>
      <SectionHeader
        title="Ban Appeals"
        icon={Gavel}
        iconClassName="text-purple-400"
        count={appeals.length}
        actions={
          <Link
            to={`/appeals?user_uuid=${userUuid}`}
            className="glass-button-sm"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Full View
          </Link>
        }
      />

      {appeals.length === 0 ? (
        <p className="text-sm text-glass-400">No ban appeals found for this player.</p>
      ) : (
        <div className="space-y-4">
          {/* Quick stats */}
          <div className="flex flex-wrap gap-1.5">
            {activeAppeals.length > 0 && (
              <span className="badge badge-orange">
                <UserPlus className="w-3 h-3" />
                {activeAppeals.length} active
              </span>
            )}
            {approvedCount > 0 && (
              <span className="badge badge-success">
                <CheckCircle className="w-3 h-3" />
                {approvedCount} approved
              </span>
            )}
            {deniedCount > 0 && (
              <span className="badge badge-danger">
                <XCircle className="w-3 h-3" />
                {deniedCount} denied
              </span>
            )}
          </div>

          {/* Active appeals first */}
          {activeAppeals.length > 0 && (
            <div className="space-y-2">
              <h4 className="eyebrow">Active Appeals</h4>
              {activeAppeals.map(appeal => (
                <AppealRow key={appeal.id} appeal={appeal} />
              ))}
            </div>
          )}

          {/* Closed appeals */}
          {closedAppeals.length > 0 && (
            <div className="space-y-2">
              <h4 className="eyebrow">Past Appeals</h4>
              {closedAppeals.map(appeal => (
                <AppealRow key={appeal.id} appeal={appeal} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const AppealRow = memo(({ appeal }: { appeal: AppealsByPlayer }) => {
  const phase = STATUS_PHASE[appeal.status] || 'terminal';
  const actionText = STATUS_ACTION[appeal.status] || '';
  const banReasonDisplay = decodeHtmlEntities(appeal.ban_reason);
  const decisionReasonDisplay = appeal.decision_reason ? decodeHtmlEntities(appeal.decision_reason) : null;
  const v2Outcome = getV2Outcome(appeal.v2_stage1_result, appeal.v2_stage2_result);

  return (
    <Link
      to={`/appeals/${appeal.id}`}
      className={cn(
        'block subcard p-3 hover:border-glass-200/20 hover:bg-glass-100/10 transition-colors group',
        phase === 'staff' && 'border-orange-500/20',
        phase === 'bot' && 'border-purple-500/20',
      )}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <PhaseIcon status={appeal.status} />
          <span className="text-sm font-semibold text-white tabular-nums">Appeal #{appeal.id}</span>
          <AppealStatusBadge status={appeal.status} />
          <AppealVersionBadge appeal={appeal} />
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-glass-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>

      <p className="text-xs text-glass-300 bg-glass-100/5 border border-glass-200/10 px-2 py-1.5 rounded-md mb-2 truncate" title={banReasonDisplay}>{banReasonDisplay}</p>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-glass-400">
        <span className={cn(
          'font-medium',
          phase === 'staff' ? 'text-orange-400' :
          phase === 'bot' ? 'text-purple-400' :
          'text-glass-400'
        )}>
          {actionText}
        </span>
        <span className="text-glass-200/20">|</span>
        <div className="flex items-center gap-1 tabular-nums">
          <Clock className="w-3 h-3" />
          <span>{formatDistanceToNow(new Date(appeal.created_at), { addSuffix: true })}</span>
        </div>
        <div className="flex items-center gap-1">
          {appeal.ban_scope === 'global' || appeal.ban_scope === 'universal'
            ? <Globe className="w-3 h-3" />
            : <Server className="w-3 h-3" />}
          <span className="capitalize">{appeal.ban_scope}</span>
        </div>
        {appeal.verdict && (
          <span className={cn(
            'font-semibold',
            appeal.verdict === 'overturned' ? 'text-green-400' : 'text-red-400'
          )}>
            Verdict: {appeal.verdict}
          </span>
        )}
        {v2Outcome && (
          <span className={cn('font-semibold', V2_OUTCOME_META[v2Outcome].text)}>
            Outcome: {V2_OUTCOME_META[v2Outcome].label}
          </span>
        )}
        {appeal.decided_at && (
          <span>Decided {formatDistanceToNow(new Date(appeal.decided_at), { addSuffix: true })}</span>
        )}
        {decisionReasonDisplay && (
          <span className="text-glass-300 truncate max-w-[200px]" title={decisionReasonDisplay}>
            {decisionReasonDisplay}
          </span>
        )}
      </div>
    </Link>
  );
});
